import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { handleNameCorrections } from '../../utils/nameCorrections';
import {
  Plus, Edit, Trash2, MapPin, AlertTriangle, Settings, FileText,
  Search, Filter, Loader2, CheckCircle2, Euro, Calendar, Building2,
  Download, Send, RefreshCw, X, Sparkles, Upload, Archive, FileDown, RotateCcw, Scale
} from 'lucide-react';
import RuleFormModal from './tourist-tax/RuleFormModal';
import ReportFormModal from './tourist-tax/ReportFormModal';
import TouristTaxReconciliationTab from './tourist-tax/TouristTaxReconciliationTab';

const API = process.env.REACT_APP_BACKEND_URL;

const FREQ_LABELS = {
  monthly: 'Mensile',
  bimonthly: 'Bimestrale',
  quarterly: 'Trimestrale',
  quadrimonthly: 'Quadrimestrale',
  semiannual: 'Semestrale',
  annual: 'Annuale',
};

const METHOD_LABELS = {
  sepa: 'Bonifico SEPA',
  f24: 'F24',
  pagopa: 'PagoPA',
  other: 'Altro',
};

const STATUS_LABELS = {
  draft: { label: 'Bozza', color: 'bg-slate-100 text-slate-700' },
  ready_to_pay: { label: 'Pronto al pagamento', color: 'bg-amber-100 text-amber-800' },
  pay_owner: { label: 'Da pagare al proprietario', color: 'bg-violet-100 text-violet-700' },
  paid: { label: 'Pagato', color: 'bg-emerald-100 text-emerald-700' },
};

const PAYMENT_METHOD_LABELS = {
  sepa: { label: 'SEPA', color: 'bg-blue-100 text-blue-700', icon: '🏦' },
  f24: { label: 'F24', color: 'bg-purple-100 text-purple-700', icon: '📋' },
  pagopa: { label: 'PagoPA', color: 'bg-pink-100 text-pink-700', icon: '💳' },
  other: { label: 'Altro', color: 'bg-slate-100 text-slate-700', icon: '📄' },
};

export default function TouristTaxManager() {
  const [tab, setTab] = useState(() => sessionStorage.getItem('tourist-tax-active-tab') || 'reconciliation');
  const changeTab = (value) => {
    setTab(value);
    sessionStorage.setItem('tourist-tax-active-tab', value);
  };
  return (
    <Layout>
      <div className="p-6 max-w-[1600px] mx-auto" data-testid="tourist-tax-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Euro className="h-7 w-7 text-emerald-600" />
              Tassa di Soggiorno (IDS)
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Gestione regole comunali e report periodici della Imposta di Soggiorno
            </p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={changeTab} className="space-y-4">
          <TabsList className="bg-white shadow-sm border h-auto flex flex-wrap justify-start">
            <TabsTrigger value="reconciliation" data-testid="tab-reconciliation">
              <Scale className="h-4 w-4 mr-2 text-sky-700" />
              Riconciliazione & Anomalie
            </TabsTrigger>
            <TabsTrigger value="rules" data-testid="tab-rules">
              <Settings className="h-4 w-4 mr-2" />
              Regole Comunali
            </TabsTrigger>
            <TabsTrigger value="without-rules" data-testid="tab-without-rules">
              <AlertTriangle className="h-4 w-4 mr-2 text-amber-600" />
              Comuni senza regole
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">
              <FileText className="h-4 w-4 mr-2" />
              Report Periodici
            </TabsTrigger>
            <TabsTrigger value="xml-batches" data-testid="tab-xml-batches">
              <Download className="h-4 w-4 mr-2" />
              XML Bonifici
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reconciliation">
            <TouristTaxReconciliationTab />
          </TabsContent>
          <TabsContent value="rules">
            <RulesTab onSwitchToReport={() => setTab('reports')} />
          </TabsContent>
          <TabsContent value="without-rules">
            <ComuniWithoutRulesTab onCreated={() => setTab('rules')} />
          </TabsContent>
          <TabsContent value="reports">
            <ReportsTab />
          </TabsContent>
          <TabsContent value="xml-batches">
            <XmlBatchesTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

// ============================================================================
// TAB: REGOLE COMUNALI
// ============================================================================
function RulesTab() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterComune, setFilterComune] = useState('');
  const [filterMethod, setFilterMethod] = useState('all');
  const [filterFreq, setFilterFreq] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const r = await fetch(`${API}/api/tourist-tax/rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) setRules(await r.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const filtered = useMemo(() => {
    return rules.filter(r => {
      if (filterComune && !r.comune_name.toLowerCase().includes(filterComune.toLowerCase())) return false;
      if (filterMethod !== 'all' && r.payment_method !== filterMethod) return false;
      if (filterFreq !== 'all' && r.payment_frequency !== filterFreq) return false;
      if (filterActive === 'active' && !r.is_active) return false;
      if (filterActive === 'inactive' && r.is_active) return false;
      return true;
    });
  }, [rules, filterComune, filterMethod, filterFreq, filterActive]);

  // Rilevo duplicati: comuni con più di una regola (case-insensitive)
  const duplicatesSet = useMemo(() => {
    const counts = new Map();
    rules.forEach(r => {
      const k = (r.comune_name || '').toLowerCase();
      counts.set(k, (counts.get(k) || 0) + 1);
    });
    return new Set([...counts.entries()].filter(([_, n]) => n > 1).map(([k]) => k));
  }, [rules]);

  const handleDelete = async (id) => {
    if (!window.confirm('Disattivare questa regola? La regola non verrà cancellata ma marcata inattiva.')) return;
    const token = localStorage.getItem('hw_token');
    const r = await fetch(`${API}/api/tourist-tax/rules/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
    });
    if (r.ok) { toast.success('Regola disattivata'); fetchRules(); }
    else toast.error('Errore disattivazione');
  };

  return (
    <Card className="p-6">
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Cerca per comune..."
            className="pl-9"
            value={filterComune}
            onChange={(e) => setFilterComune(e.target.value)}
            data-testid="filter-comune-input"
          />
        </div>
        <Select value={filterMethod} onValueChange={setFilterMethod}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Metodo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i metodi</SelectItem>
            <SelectItem value="sepa">SEPA</SelectItem>
            <SelectItem value="f24">F24</SelectItem>
            <SelectItem value="pagopa">PagoPA</SelectItem>
            <SelectItem value="other">Altro</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterFreq} onValueChange={setFilterFreq}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Frequenza" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le frequenze</SelectItem>
            <SelectItem value="monthly">Mensile</SelectItem>
            <SelectItem value="bimonthly">Bimestrale</SelectItem>
            <SelectItem value="quarterly">Trimestrale</SelectItem>
            <SelectItem value="quadrimonthly">Quadrimestrale</SelectItem>
            <SelectItem value="semiannual">Semestrale</SelectItem>
            <SelectItem value="annual">Annuale</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterActive} onValueChange={setFilterActive}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="active">Attive</SelectItem>
            <SelectItem value="inactive">Disattive</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button onClick={() => setCreating(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-rule-btn">
          <Plus className="h-4 w-4 mr-1" /> Nuova Regola
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Settings className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <p>Nessuna regola trovata.</p>
          <p className="text-xs mt-1">Clicca "Nuova Regola" o vai a "Comuni senza regole" per iniziare.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Comune</th>
                <th className="text-left px-3 py-2">Metodo</th>
                <th className="text-left px-3 py-2">Frequenza</th>
                <th className="text-left px-3 py-2">Tipo Calcolo</th>
                <th className="text-left px-3 py-2">IBAN</th>
                <th className="text-left px-3 py-2">Stato</th>
                <th className="text-right px-3 py-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const isDuplicate = duplicatesSet.has((r.comune_name || '').toLowerCase());
                return (
                <tr key={r.id} className={`border-b hover:bg-slate-50 ${isDuplicate ? 'bg-amber-50/40' : ''} ${r.tax_not_applicable ? 'bg-red-50/30' : ''}`} data-testid={`rule-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium">
                    {r.comune_name}
                    {r.province && <span className="text-xs text-slate-500 ml-1">({r.province})</span>}
                    {r.tax_not_applicable && (
                      <Badge className="ml-2 bg-red-100 text-red-700 border border-red-200 text-[10px]" data-testid={`exempt-badge-${r.id}`} title="Questo comune non ha tassa di soggiorno">
                        🚫 Esente IDS
                      </Badge>
                    )}
                    {isDuplicate && (
                      <Badge className="ml-2 bg-amber-100 text-amber-800 text-[10px]" title="Esistono più regole per questo comune: verifica quale attivare">
                        ⚠ Duplicato
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {r.tax_not_applicable ? (
                      <span className="text-xs text-slate-400 italic">—</span>
                    ) : (
                      <Badge variant="outline">{METHOD_LABELS[r.payment_method] || r.payment_method}</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">{r.tax_not_applicable ? <span className="text-xs text-slate-400 italic">—</span> : (FREQ_LABELS[r.payment_frequency] || r.payment_frequency)}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.tax_not_applicable ? (
                      <span className="text-slate-400 italic">Nessun calcolo</span>
                    ) : (
                      r.calculation_type === 'flat_per_booking' ? '🏠 Forfettario/alloggio' :
                      r.calculation_type === 'flat_per_night' ? '🛏️ Forfettario/notte' :
                      r.calculation_type === 'per_person_booking' ? '💰 Forfettario/persona' :
                      '🌙 Per persona/notte'
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600">
                    {r.tax_not_applicable ? (
                      <span className="text-slate-400 italic">—</span>
                    ) : (
                      r.iban ? `${r.iban.substring(0, 10)}...${r.iban.slice(-4)}` : <span className="text-red-500">manca</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {r.is_active ? <Badge className="bg-emerald-100 text-emerald-700">Attiva</Badge> : <Badge className="bg-slate-200 text-slate-600">Disattiva</Badge>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => setEditing(r)} data-testid={`edit-rule-${r.id}`}>
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="outline" className="ml-1 text-red-600" onClick={() => handleDelete(r.id)} data-testid={`delete-rule-${r.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <RuleFormModal
          rule={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); fetchRules(); }}
        />
      )}
    </Card>
  );
}

// ============================================================================
// TAB: COMUNI SENZA REGOLE
// ============================================================================
function FixComuneModal({ prop, onClose, onSaved }) {
  const [city, setCity] = useState(prop.city || '');
  const [province, setProvince] = useState(prop.province || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!city.trim()) { toast.error('Inserisci il nome del comune'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('hw_token');
      const r = await fetch(`${API}/api/tourist-tax/property-comune`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ property_id: prop.id || prop.property_id, city: city.trim(), province: province.trim() || null }),
      });
      const data = await r.json();
      if (r.ok) { toast.success(data.message); onSaved(); }
      else toast.error(data.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="fix-comune-modal">
        <DialogHeader><DialogTitle>Correggi comune — {prop.property_code ? `${prop.property_code} · ` : ''}{prop.name || prop.property_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {prop.address && <p className="text-xs text-slate-500">Indirizzo in scheda: {prop.address}</p>}
          <div>
            <label className="text-xs font-medium text-slate-600">Comune</label>
            <Input value={city} onChange={e => setCity(e.target.value)} placeholder="Es. La Thuile" data-testid="fix-comune-city-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Provincia (sigla)</label>
            <Input value={province} onChange={e => setProvince(e.target.value)} placeholder="Es. AO" maxLength={2} className="w-24" data-testid="fix-comune-province-input" />
          </div>
          <p className="text-[11px] text-slate-400">La correzione è permanente: il sync notturno dal vecchio sito non sovrascrive il comune già compilato.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="fix-comune-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salva comune'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ComuniWithoutRulesTab({ onCreated }) {
  const [items, setItems] = useState([]);
  const [uncoveredProps, setUncoveredProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [createFor, setCreateFor] = useState(null);
  const [subTab, setSubTab] = useState('comuni'); // 'comuni' | 'immobili'
  const [fixProp, setFixProp] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const doSearch = async () => {
    if (searchQ.trim().length < 2) { toast.error('Scrivi almeno 2 caratteri'); return; }
    setSearching(true);
    try {
      const token = localStorage.getItem('hw_token');
      const r = await fetch(`${API}/api/tourist-tax/property-search?q=${encodeURIComponent(searchQ.trim())}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) setSearchResults(await r.json());
    } finally { setSearching(false); }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const [r1, r2] = await Promise.all([
        fetch(`${API}/api/tourist-tax/comuni-without-rules`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/tourist-tax/properties-uncovered`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (r1.ok) setItems(await r1.json());
      if (r2.ok) setUncoveredProps(await r2.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = items.filter(i =>
    !filter || (i.comune_name || '').toLowerCase().includes(filter.toLowerCase())
  );

  // Raggruppa immobili scoperti per problema
  const propsWithSuggestion = uncoveredProps.filter(p => p.suggested_rules && p.suggested_rules.length > 0);
  const propsNoCity = uncoveredProps.filter(p => !p.city);
  const propsOther = uncoveredProps.filter(p => p.city && (!p.suggested_rules || p.suggested_rules.length === 0));

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-lg">Copertura regole IDS</h3>
          <p className="text-sm text-slate-500">
            {items.length} comuni in attesa di configurazione · {uncoveredProps.length} immobili scoperti
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-missing">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-4 border-b">
        <button
          onClick={() => setSubTab('comuni')}
          className={`px-3 py-2 text-sm font-medium border-b-2 ${subTab === 'comuni' ? 'border-amber-500 text-amber-700' : 'border-transparent text-slate-500'}`}
          data-testid="subtab-comuni"
        >
          <AlertTriangle className="h-4 w-4 inline mr-1" />
          Comuni senza regola ({items.length})
        </button>
        <button
          onClick={() => setSubTab('immobili')}
          className={`px-3 py-2 text-sm font-medium border-b-2 ${subTab === 'immobili' ? 'border-red-500 text-red-700' : 'border-transparent text-slate-500'}`}
          data-testid="subtab-immobili"
        >
          <Building2 className="h-4 w-4 inline mr-1" />
          Immobili scoperti ({uncoveredProps.length})
        </button>
      </div>

      {/* Ricerca immobile per correzione comune (anche se già coperto da una regola) */}
      <Card className="p-3 bg-slate-50 border-slate-200 mb-4">
        <p className="text-xs font-semibold text-slate-600 mb-2">🔍 Comune sbagliato su un immobile? Cercalo e correggilo (funziona anche se l'immobile risulta già coperto)</p>
        <div className="flex gap-2">
          <Input placeholder="Codice, nome, città o indirizzo... (es. IVA6104 o Thuile)" value={searchQ}
            onChange={e => setSearchQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && doSearch()}
            className="max-w-md" data-testid="comune-search-input" />
          <Button onClick={doSearch} disabled={searching} variant="outline" data-testid="comune-search-btn">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
        {searchResults !== null && (
          <div className="mt-3 space-y-1.5" data-testid="comune-search-results">
            {searchResults.length === 0 ? (
              <p className="text-xs text-slate-400">Nessun immobile trovato.</p>
            ) : searchResults.map(r => (
              <div key={r.id} className="flex items-center justify-between gap-3 bg-white border rounded-lg px-3 py-2">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-slate-800">{r.property_code} · {r.name}</span>
                  <span className="text-xs text-slate-500 ml-2">
                    Comune: <code className="bg-slate-100 px-1 rounded">{r.city || '—'}</code>{r.province && ` (${r.province})`}
                  </span>
                  {r.has_active_rule
                    ? <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-[10px]">coperto</Badge>
                    : <Badge className="ml-2 bg-amber-100 text-amber-800 text-[10px]">scoperto</Badge>}
                </div>
                <Button size="sm" variant="outline" onClick={() => setFixProp(r)} data-testid={`fix-comune-${r.property_code}`}>
                  <Edit className="h-3 w-3 mr-1" /> Correggi comune
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" /></div>
      ) : subTab === 'comuni' ? (
        // ============ COMUNI ============
        <>
          <Input placeholder="Filtra comune..." value={filter} onChange={(e) => setFilter(e.target.value)} className="w-[260px] mb-3" />
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-emerald-600">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3" />
              <p className="font-medium">Tutti i comuni hanno una regola attiva configurata!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map(c => (
                <Card key={c.comune_name} className="p-3 border-amber-200 bg-amber-50/30" data-testid={`comune-missing-${c.comune_name}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5">
                        <MapPin className="h-4 w-4 text-amber-600" />
                        <strong className="text-slate-800">{c.comune_name}</strong>
                        {c.province && <span className="text-xs text-slate-500">({c.province})</span>}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {c.properties_count} immobili · ISTAT {c.istat_code || '—'}
                      </p>
                    </div>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setCreateFor(c)} data-testid={`configure-${c.comune_name}`}>
                      Configura
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      ) : (
        // ============ IMMOBILI SCOPERTI ============
        <div className="space-y-5">
          {uncoveredProps.length === 0 ? (
            <div className="text-center py-12 text-emerald-600">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3" />
              <p className="font-medium">Tutti gli immobili sono coperti da una regola attiva!</p>
            </div>
          ) : (
            <>
              {propsWithSuggestion.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-orange-700 mb-2 flex items-center gap-1">
                    🔧 Probabili typo nel nome città ({propsWithSuggestion.length})
                  </h4>
                  <p className="text-xs text-slate-500 mb-2">
                    Questi immobili hanno la <code>city</code> simile a un comune con regola attiva. Probabilmente è solo uno spazio in più, un suffisso, o un typo. Correggi la scheda immobile per associarli.
                  </p>
                  <div className="space-y-2">
                    {propsWithSuggestion.map(p => (
                      <Card key={p.property_id} className="p-3 border-orange-200 bg-orange-50/40" data-testid={`uncovered-prop-${p.property_id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <strong className="text-slate-800">{p.property_name}</strong>
                            <p className="text-xs text-slate-600 mt-0.5">
                              City attuale: <code className="bg-white px-1 py-0.5 rounded">{p.city}</code>
                              {p.province && <span className="ml-2 text-slate-400">({p.province})</span>}
                            </p>
                            <p className="text-[11px] text-slate-500 mt-1">{p.reason}</p>
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {p.suggested_rules.map((s, idx) => (
                                <Badge key={idx} className="bg-emerald-100 text-emerald-800 text-[10px]">
                                  Forse: <strong className="ml-1">{s.comune_name}</strong> <span className="ml-1 opacity-60">[{s.match}]</span>
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1.5 whitespace-nowrap">
                            <Button size="sm" variant="outline" onClick={() => setFixProp(p)} data-testid={`fix-uncovered-${p.property_id}`}>
                              <Edit className="h-3 w-3 mr-1" /> Correggi comune
                            </Button>
                            <a
                              href={`/admin/properties/${p.property_id}`}
                              className="text-xs text-blue-600 hover:underline"
                              data-testid={`fix-property-${p.property_id}`}
                            >
                              Apri scheda →
                            </a>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {propsNoCity.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-red-700 mb-2 flex items-center gap-1">
                    ❌ Immobili senza city ({propsNoCity.length})
                  </h4>
                  <p className="text-xs text-slate-500 mb-2">Manca il campo "città" nella scheda. Impossibile applicare regole IDS.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {propsNoCity.map(p => (
                      <Card key={p.property_id} className="p-2 border-red-200 bg-red-50/40">
                        <div className="flex items-center justify-between gap-2">
                          <strong className="text-sm text-slate-800">{p.property_name}</strong>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setFixProp(p)} data-testid={`fix-nocity-${p.property_id}`}>
                            <Edit className="h-3 w-3 mr-1" /> Inserisci comune
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {propsOther.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">
                    ℹ️ Comune presente ma nessuna regola attiva ({propsOther.length})
                  </h4>
                  <p className="text-xs text-slate-500 mb-2">
                    Questi comuni esistono ma non hanno una regola IDS attiva. Vai nel tab "Comuni senza regola" o "Regole" per configurarli.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[400px] overflow-y-auto">
                    {propsOther.slice(0, 60).map(p => (
                      <Card key={p.property_id} className="p-2 border-slate-200">
                        <div className="text-sm font-medium text-slate-800">{p.property_name}</div>
                        <div className="text-xs text-slate-500">{p.city}{p.province && ` (${p.province})`}</div>
                      </Card>
                    ))}
                    {propsOther.length > 60 && (
                      <div className="text-xs text-slate-400 col-span-full py-2 text-center">
                        ... e altri {propsOther.length - 60} immobili (configura le regole comunali per coprirli)
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {fixProp && (
        <FixComuneModal
          prop={fixProp}
          onClose={() => setFixProp(null)}
          onSaved={() => { setFixProp(null); setSearchResults(null); setSearchQ(''); fetchData(); onCreated && onCreated(); }}
        />
      )}

      {createFor && (
        <RuleFormModal
          prefill={{
            comune_name: createFor.comune_name,
            province: createFor.province,
            region: createFor.region,
            comune_istat_code: createFor.istat_code,
          }}
          onClose={() => setCreateFor(null)}
          onSaved={() => { setCreateFor(null); fetchData(); onCreated && onCreated(); }}
        />
      )}
    </Card>
  );
}

// ============================================================================
// TAB: REPORT PERIODICI
// ============================================================================
function ReportsTab() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterComune, setFilterComune] = useState('');
  const [filterMethod, setFilterMethod] = useState('all');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [uploadingReportId, setUploadingReportId] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const params = new URLSearchParams();
      if (filterYear) params.set('year', filterYear);
      if (filterMonth !== 'all') params.set('month', filterMonth);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterComune) params.set('comune', filterComune);
      const r = await fetch(`${API}/api/tourist-tax/reports?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) setReports(await r.json());
    } finally { setLoading(false); }
  }, [filterYear, filterMonth, filterStatus, filterComune]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const generateXML = async () => {
    if (selectedIds.size === 0) { toast.error('Seleziona almeno un report'); return; }
    const token = localStorage.getItem('hw_token');
    const r = await fetch(`${API}/api/tourist-tax/generate-sepa-xml`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ report_ids: Array.from(selectedIds) }),
    });
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      const msg = typeof e.detail === 'string' ? e.detail : JSON.stringify(e.detail || {});
      toast.error(`Errore XML: ${msg}`);
      return;
    }
    const data = await r.json();
    // Download
    const blob = new Blob([data.xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `IDS_${data.batch_id}.xml`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`XML generato: ${data.nb_txs} transazioni, totale €${data.total_amount.toFixed(2)}`);
    if (data.excluded_sepa?.length) {
      toast.warning(`${data.excluded_sepa.length} pagamenti esclusi: coordinate non valide o bonifico extra-SEPA da gestire separatamente.`, { duration: 9000 });
    }
    setSelectedIds(new Set());
    fetchData();
  };

  // Crea una voce/lotto in Archivio Pagamenti per metodi NON-SEPA (F24/PagoPA): niente XML,
  // serve a caricare poi un'unica contabile per tutti i report selezionati.
  const createPaymentBatch = async () => {
    if (selectedIds.size === 0) { toast.error('Seleziona almeno un report'); return; }
    const token = localStorage.getItem('hw_token');
    try {
      const r = await fetch(`${API}/api/tourist-tax/payment-batches`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_ids: Array.from(selectedIds) }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail || {});
        toast.error(`Errore: ${msg}`);
        return;
      }
      toast.success(`Voce creata in Archivio Pagamenti (${data.nb_txs} report, € ${(data.total_amount || 0).toFixed(2)}). Carica lì un'unica contabile.`);
      setSelectedIds(new Set());
      fetchData();
    } catch { toast.error('Errore creazione voce'); }
  };

  // Bulk recompute: ricalcola TUTTI i report non-pagati visibili dal filtro corrente
  const handleBulkRecompute = async () => {
    const targets = reports.filter(r => r.status !== 'paid');
    if (targets.length === 0) {
      toast.info('Nessun report aperto da ricalcolare');
      return;
    }
    if (!window.confirm(`Ricalcolare ${targets.length} report? L'operazione aggiornerà adulti/bambini/esenti dai documenti pre-check-in di ognuno e ricalcolerà i totali.`)) return;
    const token = localStorage.getItem('hw_token');
    let success = 0, fail = 0, totalEntriesUpdated = 0;
    toast.info(`Ricalcolo in corso: 0/${targets.length}...`);
    for (let i = 0; i < targets.length; i++) {
      const r = targets[i];
      try {
        const resp = await fetch(`${API}/api/tourist-tax/reports/${r.id}/recompute`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resp.ok) {
          success++;
          const data = await resp.json();
          totalEntriesUpdated += (data.autofill_stats?.updated_count || 0);
        } else {
          fail++;
        }
      } catch {
        fail++;
      }
    }
    toast.success(`✨ Bulk recompute terminato: ${success}/${targets.length} report aggiornati, ${totalEntriesUpdated} entry con doc-autofill applicato${fail ? `, ${fail} falliti` : ''}`);
    fetchData();
  };

  const fmtDateTime = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  // Bulk: segna pagati senza contabile
  const handleBulkMarkPaid = async () => {
    if (selectedIds.size === 0) { toast.error('Seleziona almeno un report'); return; }
    if (!window.confirm(`Segnare come PAGATI ${selectedIds.size} report selezionati? (senza contabile allegata)`)) return;
    const token = localStorage.getItem('hw_token');
    try {
      const r = await fetch(`${API}/api/tourist-tax/reports/bulk-mark-paid`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_ids: Array.from(selectedIds) }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(data.detail || 'Errore'); return; }
      toast.success(`${data.updated || 0} report segnati come pagati`);
      setSelectedIds(new Set());
      fetchData();
    } catch { toast.error('Errore di rete'); }
  };

  // Bulk: una contabile per più report selezionati → tutti pagati
  const handleBulkUploadReceipt = async (file) => {
    if (!file) return;
    if (selectedIds.size === 0) { toast.error('Seleziona almeno un report'); return; }
    const token = localStorage.getItem('hw_token');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('report_ids', Array.from(selectedIds).join(','));
    setBulkUploading(true);
    try {
      const r = await fetch(`${API}/api/tourist-tax/reports/bulk-upload-receipt`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(data.detail || `Errore ${r.status}`); return; }
      toast.success(`Contabile associata a ${data.updated || 0} report → marcati pagati`);
      setSelectedIds(new Set());
      fetchData();
    } catch { toast.error('Errore upload contabile'); }
    finally { setBulkUploading(false); }
  };

  // Per riga: carica contabile su singolo report
  const handleRowUploadReceipt = async (reportId, file) => {
    if (!file) return;
    const token = localStorage.getItem('hw_token');
    const fd = new FormData();
    fd.append('file', file);
    setUploadingReportId(reportId);
    try {
      const r = await fetch(`${API}/api/tourist-tax/reports/${reportId}/upload-receipt`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(data.detail || `Errore ${r.status}`); return; }
      toast.success('Contabile caricata → report pagato');
      fetchData();
    } catch { toast.error('Errore upload contabile'); }
    finally { setUploadingReportId(null); }
  };

  const downloadReportReceipt = async (report) => {
    const token = localStorage.getItem('hw_token');
    const r = await fetch(`${API}/api/tourist-tax/reports/${report.id}/download-receipt`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!r.ok) { toast.error('Ricevuta non disponibile'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = report.receipt_filename || `ricevuta_${report.id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRowUnmarkPaid = async (reportId) => {
    if (!window.confirm("Annullare lo stato 'pagato' e rimuovere la contabile di questo report?")) return;
    const token = localStorage.getItem('hw_token');
    const r = await fetch(`${API}/api/tourist-tax/reports/${reportId}/unmark-paid`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }
    });
    if (!r.ok) { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Errore'); return; }
    toast.success('Stato annullato');
    fetchData();
  };

  // Report visibili dopo filtro metodo + helper select-all (solo non pagati)
  const visibleReports = reports.filter(r => filterMethod === 'all' || (r.payment_method || 'sepa') === filterMethod);
  const selectableIds = visibleReports.filter(r => r.status !== 'paid').map(r => r.id);
  const allVisibleSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(selectableIds));
  };

  return (
    <Card className="p-6">
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <Input
          type="number"
          value={filterYear}
          onChange={(e) => setFilterYear(parseInt(e.target.value) || new Date().getFullYear())}
          className="w-[100px]"
          placeholder="Anno"
          data-testid="filter-year"
        />
        <Select value={String(filterMonth)} onValueChange={(v) => setFilterMonth(v === 'all' ? 'all' : parseInt(v))}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Mese" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i mesi</SelectItem>
            {['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'].map((m, i) => (
              <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="Comune..."
          value={filterComune}
          onChange={(e) => setFilterComune(e.target.value)}
          className="w-[180px]"
          data-testid="filter-comune-report"
        />
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="draft">Bozza</SelectItem>
            <SelectItem value="ready_to_pay">Pronto al pagamento</SelectItem>
            <SelectItem value="pay_owner">Da pagare al proprietario</SelectItem>
            <SelectItem value="paid">Pagato</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterMethod} onValueChange={setFilterMethod}>
          <SelectTrigger className="w-[160px]" data-testid="filter-method"><SelectValue placeholder="Metodo Pagamento" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i metodi</SelectItem>
            {Object.entries(PAYMENT_METHOD_LABELS).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>{cfg.icon} {cfg.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {reports.filter(r => r.status !== 'paid').length > 0 && (
          <Button onClick={handleBulkRecompute} variant="outline" className="border-purple-400 text-purple-700 hover:bg-purple-50" data-testid="bulk-recompute-all-btn" title="Ricalcola adulti/bambini/esenti di tutti i report aperti dai documenti pre-check-in">
            <Sparkles className="h-4 w-4 mr-1" />
            Ricalcola Bambini Bulk
          </Button>
        )}
        {selectedIds.size > 0 && (
          <>
            {(filterMethod === 'f24' || filterMethod === 'pagopa') ? (
              <Button onClick={createPaymentBatch} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-batch-btn">
                <Archive className="h-4 w-4 mr-1" />
                Crea voce Archivio {filterMethod === 'f24' ? 'F24' : 'PagoPA'} ({selectedIds.size})
              </Button>
            ) : (
              <Button onClick={generateXML} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="generate-xml-btn">
                <Download className="h-4 w-4 mr-1" />
                Genera XML SEPA ({selectedIds.size})
              </Button>
            )}
            <label className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border bg-white hover:bg-slate-50 cursor-pointer" title="Carica UNA contabile e segna pagati i report selezionati" data-testid="bulk-upload-receipt-label">
              {bulkUploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
              Carica contabile ({selectedIds.size})
              <input
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                data-testid="bulk-upload-receipt-input"
                onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) handleBulkUploadReceipt(f); e.target.value = ''; }}
                disabled={bulkUploading}
              />
            </label>
            <Button onClick={handleBulkMarkPaid} variant="outline" className="border-emerald-400 text-emerald-700 hover:bg-emerald-50" data-testid="bulk-mark-paid-btn">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Segna pagati ({selectedIds.size})
            </Button>
          </>
        )}
        <Button onClick={() => setCreating(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-report-btn">
          <Plus className="h-4 w-4 mr-1" /> Nuovo Report
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" /></div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <p>Nessun report nel periodo selezionato.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-2 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    title="Seleziona tutti (non pagati)"
                    data-testid="select-all-reports"
                  />
                </th>
                <th className="text-left px-3 py-2">Periodo</th>
                <th className="text-left px-3 py-2">Comune</th>
                <th className="text-left px-3 py-2">Immobile</th>
                <th className="text-right px-3 py-2">Importo</th>
                <th className="text-left px-3 py-2">Metodo</th>
                <th className="text-left px-3 py-2">Stato</th>
                <th className="text-left px-3 py-2">Pagato / Contabile</th>
                <th className="text-right px-3 py-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {visibleReports.map(r => {
                const months = (r.period_months || []).map(m => ['','Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'][m]).join('/');
                const statusCfg = STATUS_LABELS[r.status] || STATUS_LABELS.draft;
                const methodKey = (r.payment_method || 'sepa').toLowerCase();
                const methodCfg = PAYMENT_METHOD_LABELS[methodKey] || PAYMENT_METHOD_LABELS.other;
                return (
                  <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`report-row-${r.id}`}>
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        disabled={r.status === 'paid'}
                        data-testid={`select-report-${r.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium">{months} {r.period_year}</td>
                    <td className="px-3 py-2">{r.comune_name}</td>
                    <td className="px-3 py-2">{r.property_name}</td>
                    <td className="px-3 py-2 text-right font-mono">€ {(r.total_final_amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2">
                      <Badge className={methodCfg.color} data-testid={`method-${r.id}`}>
                        <span className="mr-1">{methodCfg.icon}</span>{methodCfg.label}
                      </Badge>
                    </td>
                    <td className="px-3 py-2"><Badge className={statusCfg.color}>{statusCfg.label}</Badge></td>
                    <td className="px-3 py-2 text-xs" data-testid={`report-paid-cell-${r.id}`}>
                      {r.status === 'paid' ? (
                        <div className="space-y-0.5">
                          <div className="text-emerald-700 font-medium">{fmtDateTime(r.paid_at)}</div>
                          {r.receipt_filename ? (
                            <button
                              onClick={() => downloadReportReceipt(r)}
                              className="inline-flex items-center text-blue-600 hover:underline"
                              data-testid={`download-report-receipt-${r.id}`}
                              title={r.receipt_uploaded_at ? `Caricata il ${fmtDateTime(r.receipt_uploaded_at)}` : 'Scarica contabile'}
                            >
                              <FileDown className="h-3 w-3 mr-1" />
                              {r.receipt_filename.length > 18 ? `${r.receipt_filename.slice(0, 15)}…` : r.receipt_filename}
                            </button>
                          ) : (
                            <span className="text-slate-400">nessuna contabile</span>
                          )}
                          {r.receipt_uploaded_at && (
                            <div className="text-[10px] text-slate-400">car. {fmtDateTime(r.receipt_uploaded_at)}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex gap-1 justify-end">
                        {r.status !== 'paid' && (
                          <label className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border bg-white hover:bg-slate-50 cursor-pointer" title="Carica contabile e segna come pagato">
                            {uploadingReportId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.png,.jpg,.jpeg,.webp"
                              data-testid={`upload-report-receipt-${r.id}`}
                              onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) handleRowUploadReceipt(r.id, f); e.target.value = ''; }}
                              disabled={uploadingReportId === r.id}
                            />
                          </label>
                        )}
                        {r.status === 'paid' && (
                          <Button size="sm" variant="outline" onClick={() => handleRowUnmarkPaid(r.id)} title="Annulla pagato / rimuovi contabile" data-testid={`unmark-report-${r.id}`}>
                            <RotateCcw className="h-3 w-3" />
                          </Button>
                        )}
                        {r.status !== 'paid' && (
                          <Button
                            size="sm"
                            variant="outline"
                            title="Ricalcola entries con conteggi auto da documenti pre-check-in"
                            onClick={async () => {
                              const token = localStorage.getItem('hw_token');
                              try {
                                const resp = await fetch(`${API}/api/tourist-tax/reports/${r.id}/recompute`, {
                                  method: 'POST',
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                if (!resp.ok) {
                                  const e = await resp.json().catch(() => ({}));
                                  toast.error(e.detail || 'Errore ricalcolo');
                                  return;
                                }
                                const data = await resp.json();
                                const stats = data.autofill_stats || {};
                                toast.success(`Ricalcolato: ${stats.updated_count || 0} entry con documenti aggiornate${stats.unknown_dob ? `, ${stats.unknown_dob} ospiti senza data nascita` : ''}`);
                                fetchData();
                              } catch (e) {
                                toast.error('Errore di rete: ' + e.message);
                              }
                            }}
                            data-testid={`recompute-report-${r.id}`}
                          >
                            <RefreshCw className="h-3 w-3" />
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setEditing(r)} data-testid={`edit-report-${r.id}`}>
                          <Edit className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <ReportFormModal
          report={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); fetchData(); }}
        />
      )}
    </Card>
  );
}

// ============================================================================
// TAB: XML BATCHES (storico XML + ricevute + distinte per Comune)
// ============================================================================
function XmlBatchesTab() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingBatch, setUploadingBatch] = useState(null); // batch_id currently uploading receipt
  const [markPaidBatch, setMarkPaidBatch] = useState(null);   // batch shown in mark-paid dialog
  const [paymentRef, setPaymentRef] = useState('');
  const role = (typeof window !== 'undefined' && JSON.parse(localStorage.getItem('hw_user') || '{}').role) || '';
  const isAdmin = role === 'admin';

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const r = await fetch(`${API}/api/tourist-tax/xml-batches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) setBatches(await r.json());
      else toast.error('Errore caricamento batch');
    } catch (e) {
      toast.error('Errore caricamento batch');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  const downloadXml = async (batchId) => {
    const token = localStorage.getItem('hw_token');
    const r = await fetch(`${API}/api/tourist-tax/xml-batches/${batchId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!r.ok) { toast.error('Errore download'); return; }
    const data = await r.json();
    const blob = new Blob([data.xml_content], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `IDS_${batchId}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadReceipt = async (batch) => {
    const token = localStorage.getItem('hw_token');
    const r = await fetch(`${API}/api/tourist-tax/xml-batches/${batch.batch_id}/download-receipt`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!r.ok) { toast.error('Ricevuta non disponibile'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = batch.receipt_filename || `ricevuta_${batch.batch_id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadDistinteZip = async (batchId) => {
    const token = localStorage.getItem('hw_token');
    const r = await fetch(`${API}/api/tourist-tax/xml-batches/${batchId}/distinte-zip`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      toast.error(err.detail || 'Errore generazione distinte');
      return;
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `distinte_IDS_${batchId}.zip`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('ZIP distinte scaricato');
  };

  const handleUploadReceipt = async (batch, file) => {
    if (!file) return;
    const token = localStorage.getItem('hw_token');
    const fd = new FormData();
    fd.append('file', file);
    setUploadingBatch(batch.batch_id);
    try {
      const r = await fetch(`${API}/api/tourist-tax/xml-batches/${batch.batch_id}/upload-receipt`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(data.detail || `Errore ${r.status}`);
      } else {
        toast.success(`Ricevuta caricata: ${data.report_ids_updated || 0} report marcati come pagati`);
        await handleNameCorrections(data.name_corrections);
        await loadBatches();
      }
    } catch (e) {
      toast.error('Errore upload ricevuta');
    } finally {
      setUploadingBatch(null);
    }
  };

  const handleMarkPaid = async () => {
    if (!markPaidBatch) return;
    const token = localStorage.getItem('hw_token');
    try {
      const r = await fetch(`${API}/api/tourist-tax/xml-batches/${markPaidBatch.batch_id}/mark-paid`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_reference: paymentRef.trim() || null })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(data.detail || 'Errore'); return; }
      toast.success(`Batch marcato come pagato (${data.report_ids_updated || 0} report)`);
      setMarkPaidBatch(null);
      setPaymentRef('');
      await loadBatches();
    } catch (e) { toast.error('Errore'); }
  };

  const handleDeleteBatch = async (batch) => {
    if (!window.confirm(`Eliminare definitivamente il lotto ${batch.batch_id}?\nI ${batch.nb_txs} report collegati torneranno disponibili per una nuova generazione.\nL'operazione non è reversibile.`)) return;
    const token = localStorage.getItem('hw_token');
    const r = await fetch(`${API}/api/tourist-tax/xml-batches/${batch.batch_id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) {
      const data = await r.json();
      toast.success(`Lotto eliminato (${data.reports_released} report scollegati)`);
      loadBatches();
    } else {
      const err = await r.json().catch(() => ({}));
      toast.error(err.detail || 'Errore eliminazione lotto');
    }
  };

  const handleUnmarkPaid = async (batch) => {
    if (!window.confirm(`Annullare lo stato 'pagato' per il batch ${batch.batch_id}?\nLa ricevuta caricata sarà rimossa e i report torneranno a "da pagare".`)) return;
    const token = localStorage.getItem('hw_token');
    const r = await fetch(`${API}/api/tourist-tax/xml-batches/${batch.batch_id}/unmark-paid`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      toast.error(err.detail || 'Errore');
      return;
    }
    toast.success('Stato annullato');
    await loadBatches();
  };

  const fmtEur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  const fmtDate = (d) => d ? new Date(d).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '-';

  return (
    <Card className="p-6" data-testid="xml-batches-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-lg">Archivio XML SEPA & Pagamenti (ultimi 50)</h3>
          <p className="text-sm text-slate-500">Carica la ricevuta della banca per marcare i pagamenti, scarica le distinte da inviare ai Comuni.</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadBatches} data-testid="refresh-batches-btn">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Aggiorna
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" /></div>
      ) : batches.length === 0 ? (
        <p className="text-slate-500 text-center py-8">Nessun XML generato</p>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="batches-table">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Data Creazione</th>
              <th className="text-left px-3 py-2">Batch ID</th>
              <th className="text-right px-3 py-2">Tx</th>
              <th className="text-right px-3 py-2">Totale</th>
              <th className="text-center px-3 py-2">Stato</th>
              <th className="text-left px-3 py-2">Pagato il</th>
              <th className="text-right px-3 py-2 min-w-[280px]">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {batches.map(b => {
              const isPaid = b.status === 'paid';
              const hasReceipt = !!b.receipt_filename;
              return (
              <tr key={b.batch_id} className={`border-b hover:bg-slate-50 ${isPaid ? 'bg-emerald-50/30' : ''}`} data-testid={`batch-row-${b.batch_id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                <td className="px-3 py-2 font-mono text-xs">{b.batch_id}</td>
                <td className="px-3 py-2 text-right">{b.nb_txs}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtEur(b.total_amount)}</td>
                <td className="px-3 py-2 text-center">
                  {isPaid ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Pagato
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                      <AlertTriangle className="h-3 w-3 mr-1" /> Da pagare
                    </Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                  {isPaid ? fmtDate(b.paid_at) : '-'}
                  {hasReceipt && <div className="text-[10px] text-slate-400">📎 {b.receipt_filename}</div>}
                </td>
                <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => downloadXml(b.batch_id)} title="Scarica XML SEPA originale" data-testid={`download-xml-${b.batch_id}`}>
                    <Download className="h-3 w-3 mr-1" /> XML
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadDistinteZip(b.batch_id)} title="Scarica ZIP distinte PDF per ogni Comune" data-testid={`download-distinte-${b.batch_id}`}>
                    <FileDown className="h-3 w-3 mr-1" /> Distinte
                  </Button>
                  {hasReceipt && (
                    <Button size="sm" variant="outline" onClick={() => downloadReceipt(b)} title="Scarica ricevuta banca" data-testid={`download-receipt-${b.batch_id}`}>
                      <Archive className="h-3 w-3 mr-1" /> Ricevuta
                    </Button>
                  )}
                  {isAdmin && !isPaid && (
                    <>
                      <label className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border bg-white hover:bg-slate-50 cursor-pointer" title="Carica PDF/immagine ricevuta banca">
                        {uploadingBatch === b.batch_id ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Upload className="h-3 w-3 mr-1" />
                        )}
                        Carica Ricevuta
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.png,.jpg,.jpeg,.webp"
                          data-testid={`upload-receipt-${b.batch_id}`}
                          onChange={(e) => {
                            const f = e.target.files && e.target.files[0];
                            if (f) handleUploadReceipt(b, f);
                            e.target.value = '';
                          }}
                          disabled={uploadingBatch === b.batch_id}
                        />
                      </label>
                      <Button size="sm" variant="outline" onClick={() => { setMarkPaidBatch(b); setPaymentRef(''); }} title="Marca come pagato senza ricevuta (es. F24)" data-testid={`mark-paid-${b.batch_id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Marca pagato
                      </Button>
                    </>
                  )}
                  {isAdmin && isPaid && (
                    <Button size="sm" variant="outline" onClick={() => handleUnmarkPaid(b)} title="Annulla stato pagato" data-testid={`unmark-paid-${b.batch_id}`}>
                      <RotateCcw className="h-3 w-3 mr-1" /> Annulla
                    </Button>
                  )}
                  {isAdmin && !isPaid && (
                    <Button size="sm" variant="outline" onClick={() => handleDeleteBatch(b)}
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      title="Elimina lotto (i report tornano disponibili)" data-testid={`delete-batch-${b.batch_id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {/* Dialog: Marca come pagato senza ricevuta */}
      <Dialog open={!!markPaidBatch} onOpenChange={(o) => { if (!o) { setMarkPaidBatch(null); setPaymentRef(''); } }}>
        <DialogContent data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Marca batch come pagato</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {markPaidBatch && (
              <div className="text-sm text-slate-600 bg-slate-50 rounded p-3">
                <div><strong>Batch:</strong> <span className="font-mono">{markPaidBatch.batch_id}</span></div>
                <div><strong>Importo:</strong> {fmtEur(markPaidBatch.total_amount)}</div>
                <div><strong>Transazioni:</strong> {markPaidBatch.nb_txs}</div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium block mb-1">Riferimento pagamento (opzionale)</label>
              <Input
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                placeholder="es. CRO 123456789 / F24 protocollo / ricevuta PagoPA"
                data-testid="payment-ref-input"
              />
              <p className="text-xs text-slate-500 mt-1">Verrà salvato sul batch e sui report associati.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setMarkPaidBatch(null); setPaymentRef(''); }}>Annulla</Button>
              <Button onClick={handleMarkPaid} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-mark-paid-btn">
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Marca come pagato
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
